export const AUTOMATION_RULE_ENTITY_IDS = [
  'portfolio',
  'campaign',
  'placement',
  'ad_group',
  'target',
  'advertised_product',
  'search_term',
  'st_harvest',
  'term_list',
] as const;

export type AutomationRuleEntityId = (typeof AUTOMATION_RULE_ENTITY_IDS)[number];

export const AUTOMATION_ENTITY_NOUNS: Record<string, string> = {
  portfolio: 'portfolios',
  campaign: 'campaigns',
  placement: 'placements',
  ad_group: 'ad groups',
  target: 'targets',
  advertised_product: 'product ads',
  search_term: 'search terms',
  st_harvest: 'search terms',
  term_list: 'terms',
};

export const AUTOMATION_RULE_CHIPS: Array<{ id: AutomationRuleEntityId; label: string }> = [
  { id: 'portfolio', label: 'Portfolios' },
  { id: 'campaign', label: 'Campaigns' },
  { id: 'placement', label: 'Placements' },
  { id: 'ad_group', label: 'Ad Groups' },
  { id: 'target', label: 'Targets' },
  { id: 'advertised_product', label: 'Advertised Product' },
  { id: 'search_term', label: 'Search Term' },
  { id: 'st_harvest', label: 'ST Harvesting' },
  { id: 'term_list', label: 'Blacklist/Whitelist' },
];

export const AD_TYPES_BY_ENTITY: Record<string, string[]> = {
  portfolio: ['SP', 'SB', 'SD'],
  campaign: ['SP', 'SB', 'SD'],
  placement: ['SP', 'SB'],
  ad_group: ['SP', 'SB', 'SD'],
  target: ['SP', 'SB', 'SD'],
  advertised_product: ['SP'],
  search_term: ['SP'],
  st_harvest: ['SP'],
  term_list: ['SP'],
};

export function isAutomationRuleEntity(value: string | undefined | null): value is AutomationRuleEntityId {
  return Boolean(value && (AUTOMATION_RULE_ENTITY_IDS as readonly string[]).includes(value));
}

export function entityNoun(entityType: string): string {
  return AUTOMATION_ENTITY_NOUNS[entityType] || 'entities';
}

export const MAX_AND_CONDITIONS = 8;

export type BuilderConditionLeaf = {
  metric: string;
  operator: string;
  value_type: string;
  value: number | string;
};

export type PlacementValueAdjustment = {
  placement: string;
  mode: string;
  value: number;
};

export type BuilderConditionGroup = {
  kind: string;
  conditions: {
    op: string;
    children: BuilderConditionLeaf[];
  } | null;
  action: {
    type: string;
    value?: number;
    message?: string;
    adjustments?: PlacementValueAdjustment[];
    revertAtDayEnd?: boolean;
    revertValue?: number;
  };
};

const STATUS_RESTORE_ACTIONS = new Set([
  'pause_campaign',
  'enable_campaign',
  'pause_portfolio',
  'enable_portfolio',
  'pause_ad_group',
  'enable_ad_group',
  'pause_target',
  'enable_target',
  'pause_product_ad',
  'enable_product_ad',
  'pause_parent_campaign',
  'enable_parent_campaign',
]);

export function actionUsesNumericRestore(actionType: string): boolean {
  return !STATUS_RESTORE_ACTIONS.has(actionType);
}

export function catalogActionIsRevertible(
  actionType: string,
  catalog: Array<{ type: string; enabled?: boolean; revertible?: boolean }>
): boolean {
  const match = catalog.find((item) => item.type === actionType);
  return Boolean(match && match.enabled !== false && match.revertible);
}

export function preserveRevertAtDayEnd(
  next: BuilderConditionGroup['action'],
  previousFlag: boolean | undefined,
  revertible: boolean,
  previousValue?: number
): BuilderConditionGroup['action'] {
  if (revertible && previousFlag) {
    const kept: BuilderConditionGroup['action'] = { ...next, revertAtDayEnd: true };
    if (
      previousValue != null &&
      Number.isFinite(previousValue) &&
      actionUsesNumericRestore(next.type)
    ) {
      kept.revertValue = previousValue;
    } else if (!actionUsesNumericRestore(next.type)) {
      delete kept.revertValue;
    }
    return kept;
  }
  if (!('revertAtDayEnd' in next) && !('revertValue' in next)) return next;
  const { revertAtDayEnd: _flag, revertValue: _value, ...rest } = next;
  return rest;
}

const OPERATOR_SYMBOL: Record<string, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  neq: '≠',
};

export function formatPlacementValueAction(
  action: BuilderConditionGroup['action']
): string {
  const parts = (action.adjustments ?? []).map((item) => {
    const slot =
      PLACEMENT_VALUE_FIELDS.find((f) => f.id === item.placement)?.label ?? item.placement;
    const mode =
      PLACEMENT_VALUE_MODES.find((m) => m.value === item.mode)?.label ?? item.mode;
    return `${slot} ${mode} ${item.value}`;
  });
  return parts.length ? `placement_value (${parts.join(', ')})` : 'placement_value';
}

export function formatConditionLeaf(leaf: BuilderConditionLeaf): string {
  const op = OPERATOR_SYMBOL[leaf.operator] ?? leaf.operator;
  return `${String(leaf.metric).toUpperCase()} ${op} ${leaf.value}`;
}

export function formatConditionGroupLine(group: BuilderConditionGroup): string {
  const children = group.conditions?.children ?? [];
  const cond = children.length
    ? children.map(formatConditionLeaf).join(' AND ')
    : group.kind === 'else'
      ? 'always'
      : '—';
  const actionLabel =
    group.action.type === 'placement_value'
      ? formatPlacementValueAction(group.action)
      : `${group.action.type}${group.action.value != null ? ` ${group.action.value}` : ''}`;
  const revert = group.action.revertAtDayEnd
    ? group.action.revertValue != null && Number.isFinite(group.action.revertValue)
      ? ` · reverts at day end to ${group.action.revertValue}`
      : ' · reverts at day end'
    : '';
  return `${group.kind.toUpperCase()}: ${cond} → ${actionLabel}${revert}`;
}

function leaf(metric: string, operator: string, value: number) {
  return { metric, operator, value_type: 'fixed', value };
}

function ifThen(
  children: BuilderConditionLeaf[],
  action: BuilderConditionGroup['action']
): BuilderConditionGroup {
  return {
    kind: 'if',
    conditions: { op: 'and', children },
    action,
  };
}

export const PLACEMENT_VALUE_FIELDS: Array<{ id: string; label: string }> = [
  { id: 'TOP_OF_SEARCH', label: 'Top of Search' },
  { id: 'REST_OF_SEARCH', label: 'Rest of Search' },
  { id: 'PRODUCT_PAGES', label: 'Product Pages' },
];

export const PLACEMENT_VALUE_MODES: Array<{ value: string; label: string }> = [
  { value: 'SET', label: 'Set Fixed Value' },
  { value: 'INCREASE', label: 'Increase by' },
  { value: 'DECREASE', label: 'Decrease by' },
];

export function defaultPlacementAdjustments(): PlacementValueAdjustment[] {
  return [{ placement: 'TOP_OF_SEARCH', mode: 'INCREASE', value: 10 }];
}

export function defaultPlacementAction(): BuilderConditionGroup['action'] {
  return { type: 'placement_value', adjustments: defaultPlacementAdjustments() };
}

export function parsePlacementAdjustmentRows(
  action: BuilderConditionGroup['action'] | undefined
): Record<string, { mode: string; value: string }> {
  const rows: Record<string, { mode: string; value: string }> = {
    TOP_OF_SEARCH: { mode: 'SET', value: '' },
    REST_OF_SEARCH: { mode: 'SET', value: '' },
    PRODUCT_PAGES: { mode: 'SET', value: '' },
  };
  for (const item of action?.adjustments ?? []) {
    if (!rows[item.placement]) continue;
    rows[item.placement] = {
      mode: item.mode || 'SET',
      value: item.value == null ? '' : String(item.value),
    };
  }
  return rows;
}

export function serializePlacementAdjustments(
  rows: Record<string, { mode: string; value: string }>,
  options?: { disableTopOfSearch?: boolean }
): PlacementValueAdjustment[] {
  const out: PlacementValueAdjustment[] = [];
  for (const field of PLACEMENT_VALUE_FIELDS) {
    if (options?.disableTopOfSearch && field.id === 'TOP_OF_SEARCH') continue;
    const row = rows[field.id];
    if (!row?.mode) continue;
    const raw = row.value.trim();
    if (raw === '') continue;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) continue;
    out.push({ placement: field.id, mode: row.mode, value: numeric });
  }
  return out;
}

export function placementActionHasAdjustments(action: BuilderConditionGroup['action']): boolean {
  return (action.adjustments ?? []).length > 0;
}

export function normalizePlacementGroups(groups: BuilderConditionGroup[]): BuilderConditionGroup[] {
  return groups.map((group) => {
    const type = group.action.type;
    if (type === 'placement_value') return group;
    if (type === 'increase_placement_pct' || type === 'decrease_placement_pct' || type === 'set_placement_pct') {
      const mode = type.startsWith('increase') ? 'INCREASE' : type.startsWith('decrease') ? 'DECREASE' : 'SET';
      return {
        ...group,
        action: preserveRevertAtDayEnd(
          {
            type: 'placement_value',
            adjustments: [
              { placement: 'TOP_OF_SEARCH', mode, value: Number(group.action.value ?? 0) },
            ],
          },
          group.action.revertAtDayEnd,
          true,
          group.action.revertValue
        ),
      };
    }
    return {
      ...group,
      action: preserveRevertAtDayEnd(
        defaultPlacementAction(),
        group.action.revertAtDayEnd,
        true,
        group.action.revertValue
      ),
    };
  });
}

export function defaultAdTypes(entityType: string): string[] {
  return [...(AD_TYPES_BY_ENTITY[entityType] || ['SP'])];
}

export function defaultConditionGroups(entityType: string): BuilderConditionGroup[] {
  switch (entityType) {
    case 'portfolio':
      return [ifThen([leaf('acos', 'lt', 30)], { type: 'increase_budget_pct', value: 20 })];
    case 'placement':
      return [ifThen([leaf('tos_acos', 'lt', 30)], defaultPlacementAction())];
    case 'ad_group':
      return [
        ifThen(
          [leaf('acos', 'gt', 45), leaf('spend', 'gt', 100)],
          { type: 'pause_ad_group' }
        ),
      ];
    case 'target':
      return [
        ifThen(
          [leaf('acos', 'gt', 50), leaf('clicks', 'gte', 20)],
          { type: 'decrease_bid_pct', value: 15 }
        ),
      ];
    case 'advertised_product':
      return [
        ifThen(
          [leaf('acos', 'gt', 60), leaf('spend', 'gt', 200)],
          { type: 'pause_product_ad' }
        ),
      ];
    case 'search_term':
      return [
        ifThen(
          [leaf('clicks', 'gte', 20), leaf('orders', 'eq', 0)],
          { type: 'add_negative_exact' }
        ),
      ];
    case 'st_harvest':
      return [
        ifThen(
          [leaf('orders', 'gte', 3), leaf('acos', 'lt', 30)],
          { type: 'add_keyword' }
        ),
      ];
    case 'campaign':
    default:
      return [ifThen([leaf('acos', 'lt', 30)], { type: 'increase_budget_pct', value: 20 })];
  }
}

export type ScopeCopy = {
  heading: string;
  all: string;
  specific: string;
  specificAdGroups?: string;
  portfolio?: string;
  search: string;
  searchAdGroups?: string;
  searchPortfolios?: string;
};

export function scopeCopy(entityType: string): ScopeCopy {
  const noun = entityNoun(entityType);
  if (entityType === 'placement') {
    return {
      heading: 'Placements',
      all: 'All campaigns',
      specific: 'Specific campaigns',
      portfolio: 'Portfolio',
      search: 'Search campaigns…',
      searchPortfolios: 'Search portfolios…',
    };
  }
  if (entityType === 'search_term' || entityType === 'st_harvest' || entityType === 'target') {
    return {
      heading: 'Scope',
      all: 'All campaigns',
      specific: 'Specific campaigns',
      specificAdGroups: 'Specific ad groups',
      portfolio: 'Portfolio',
      search: 'Search campaigns…',
      searchAdGroups: 'Search ad groups…',
      searchPortfolios: 'Search portfolios…',
    };
  }
  if (entityType === 'advertised_product') {
    return {
      heading: 'Products',
      all: 'All advertised products',
      specific: 'Specific ASINs',
      search: 'Search ASINs…',
    };
  }
  if (entityType === 'portfolio') {
    return {
      heading: 'Portfolios',
      all: 'All portfolios',
      specific: 'Specific portfolios',
      search: 'Search portfolios…',
    };
  }
  if (entityType === 'ad_group') {
    return {
      heading: 'Ad groups',
      all: 'All ad groups',
      specific: 'Specific ad groups',
      search: 'Search ad groups…',
    };
  }
  return {
    heading: noun.charAt(0).toUpperCase() + noun.slice(1),
    all: `All ${noun}`,
    specific: `Specific ${noun}`,
    portfolio: 'Portfolio',
    search: `Search ${noun}…`,
    searchPortfolios: 'Search portfolios…',
  };
}

export type HarvestOptions = {
  matchType: string;
  bidSource: 'fixed' | 'source_keyword' | 'ad_group_default';
  bid: number | null;
  destination: 'same_ad_group' | 'specific';
  destinationAdGroupId: string;
  skipIfExists: boolean;
};

export function defaultHarvestOptions(raw?: Record<string, unknown> | null): HarvestOptions {
  const src = raw || {};
  const bidSourceRaw = String(src.bid_source || src.starting_bid || 'ad_group_default');
  const bidSource: HarvestOptions['bidSource'] =
    bidSourceRaw === 'fixed' || bidSourceRaw === 'source_keyword' ? bidSourceRaw : 'ad_group_default';
  return {
    matchType: String(src.match_type || src.matchType || 'EXACT').toUpperCase(),
    bidSource,
    bid: typeof src.bid === 'number' ? src.bid : null,
    destination: src.destination === 'specific' ? 'specific' : 'same_ad_group',
    destinationAdGroupId: String(src.destination_ad_group_id || ''),
    skipIfExists: src.skip_if_exists !== false,
  };
}

export function harvestOptionsPayload(opts: HarvestOptions): Record<string, unknown> {
  return {
    match_type: opts.matchType,
    bid_source: opts.bidSource,
    bid: opts.bidSource === 'fixed' ? opts.bid : null,
    destination: opts.destination,
    destination_ad_group_id: opts.destination === 'specific' ? opts.destinationAdGroupId : null,
    skip_if_exists: opts.skipIfExists,
  };
}

export const TARGET_MATCH_TYPES = [
  { id: 'EXACT', label: 'EXACT' },
  { id: 'BROAD', label: 'BROAD' },
  { id: 'PHRASE', label: 'PHRASE' },
  { id: 'ASIN', label: 'ASIN' },
  { id: 'CATEGORY', label: 'CATEGORY' },
  { id: 'AUDIENCE', label: 'AUDIENCE' },
  { id: 'THEME', label: 'THEME' },
  { id: 'AUTO', label: 'AUTO' },
] as const;

export type TargetMatchTypeId = (typeof TARGET_MATCH_TYPES)[number]['id'];

export const ALL_TARGET_MATCH_TYPES: TargetMatchTypeId[] = TARGET_MATCH_TYPES.map((item) => item.id);

export function defaultTargetMatchTypes(raw?: Record<string, unknown> | null): TargetMatchTypeId[] {
  const src = raw?.match_types ?? raw?.matchTypes;
  if (!Array.isArray(src) || src.length === 0) return [...ALL_TARGET_MATCH_TYPES];
  const allowed = new Set<string>(ALL_TARGET_MATCH_TYPES);
  const next = src
    .map((item) => String(item).toUpperCase())
    .filter((item): item is TargetMatchTypeId => allowed.has(item));
  return next.length ? next : [...ALL_TARGET_MATCH_TYPES];
}

export function targetMatchTypesPayload(matchTypes: string[]): Record<string, unknown> {
  return { match_types: matchTypes };
}

export function mergeAutomationRuleOptions(
  entityType: string,
  harvest: HarvestOptions,
  matchTypes: string[],
  scopeKind?: 'all' | 'campaigns' | 'ad_groups' | 'portfolios'
): Record<string, unknown> | undefined {
  const opts: Record<string, unknown> = {};
  if (entityType === 'st_harvest') Object.assign(opts, harvestOptionsPayload(harvest));
  if (entityType === 'target') Object.assign(opts, targetMatchTypesPayload(matchTypes));
  const persistSplitScope =
    (entityType === 'search_term' || entityType === 'st_harvest' || entityType === 'target') &&
    scopeKind &&
    scopeKind !== 'all';
  const persistPortfolioScope =
    (entityType === 'campaign' || entityType === 'placement') && scopeKind === 'portfolios';
  if (persistSplitScope || persistPortfolioScope) {
    opts.scope_kind =
      scopeKind === 'ad_groups' ? 'ad_group' : scopeKind === 'portfolios' ? 'portfolio' : 'campaign';
  }
  return Object.keys(opts).length ? opts : undefined;
}

export type TargetActionFamily =
  | 'bid_change'
  | 'status_change'
  | 'parent_campaign_budget'
  | 'parent_campaign_status'
  | 'send_alert';

export type TargetActionMethod =
  | 'increase_value'
  | 'decrease_value'
  | 'increase_pct'
  | 'decrease_pct'
  | 'set_fixed'
  | 'set_suggested';

export const TARGET_ACTION_FAMILIES: Array<{ value: TargetActionFamily; label: string }> = [
  { value: 'bid_change', label: 'Bid Change' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'parent_campaign_budget', label: 'Parent Campaign Budget' },
  { value: 'parent_campaign_status', label: 'Parent Campaign Status' },
  { value: 'send_alert', label: 'Send Alert' },
];

export const TARGET_BID_METHODS: Array<{ value: TargetActionMethod; label: string }> = [
  { value: 'increase_value', label: 'Increase by Value' },
  { value: 'decrease_value', label: 'Decrease by Value' },
  { value: 'increase_pct', label: 'Increase by %' },
  { value: 'decrease_pct', label: 'Decrease by %' },
  { value: 'set_fixed', label: 'Set Fixed' },
  { value: 'set_suggested', label: 'Set Suggested Bid' },
];

export const TARGET_BUDGET_METHODS: Array<{ value: TargetActionMethod; label: string }> = [
  { value: 'increase_value', label: 'Increase by Value' },
  { value: 'decrease_value', label: 'Decrease by Value' },
  { value: 'increase_pct', label: 'Increase by %' },
  { value: 'decrease_pct', label: 'Decrease by %' },
  { value: 'set_fixed', label: 'Set Fixed' },
];

const BID_METHOD_TO_TYPE: Record<Exclude<TargetActionMethod, 'set_suggested'>, string> = {
  increase_value: 'increase_bid_amount',
  decrease_value: 'decrease_bid_amount',
  increase_pct: 'increase_bid_pct',
  decrease_pct: 'decrease_bid_pct',
  set_fixed: 'set_bid',
};

const BUDGET_METHOD_TO_TYPE: Record<Exclude<TargetActionMethod, 'set_suggested'>, string> = {
  increase_value: 'increase_parent_budget_amount',
  decrease_value: 'decrease_parent_budget_amount',
  increase_pct: 'increase_parent_budget_pct',
  decrease_pct: 'decrease_parent_budget_pct',
  set_fixed: 'set_parent_budget',
};

const TYPE_TO_BID_METHOD: Record<string, TargetActionMethod> = {
  increase_bid_amount: 'increase_value',
  decrease_bid_amount: 'decrease_value',
  increase_bid_pct: 'increase_pct',
  decrease_bid_pct: 'decrease_pct',
  set_bid: 'set_fixed',
  set_suggested_bid: 'set_suggested',
};

const TYPE_TO_BUDGET_METHOD: Record<string, TargetActionMethod> = {
  increase_parent_budget_amount: 'increase_value',
  decrease_parent_budget_amount: 'decrease_value',
  increase_parent_budget_pct: 'increase_pct',
  decrease_parent_budget_pct: 'decrease_pct',
  set_parent_budget: 'set_fixed',
};

export type ParsedTargetAction = {
  family: TargetActionFamily;
  method: TargetActionMethod;
  status: 'ENABLED' | 'PAUSED';
  value: number;
};

export function parseTargetAction(action: BuilderConditionGroup['action'] | undefined): ParsedTargetAction {
  const type = action?.type || 'decrease_bid_pct';
  if (type === 'send_alert') {
    return { family: 'send_alert', method: 'decrease_pct', status: 'ENABLED', value: Number(action?.value ?? 0) };
  }
  if (type === 'enable_target' || type === 'pause_target') {
    return {
      family: 'status_change',
      method: 'decrease_pct',
      status: type === 'enable_target' ? 'ENABLED' : 'PAUSED',
      value: 0,
    };
  }
  if (type === 'enable_parent_campaign' || type === 'pause_parent_campaign') {
    return {
      family: 'parent_campaign_status',
      method: 'decrease_pct',
      status: type === 'enable_parent_campaign' ? 'ENABLED' : 'PAUSED',
      value: 0,
    };
  }
  if (TYPE_TO_BUDGET_METHOD[type]) {
    return {
      family: 'parent_campaign_budget',
      method: TYPE_TO_BUDGET_METHOD[type],
      status: 'ENABLED',
      value: Number(action?.value ?? 0),
    };
  }
  return {
    family: 'bid_change',
    method: TYPE_TO_BID_METHOD[type] ?? 'decrease_pct',
    status: 'ENABLED',
    value: Number(action?.value ?? 0),
  };
}

export function serializeTargetAction(parsed: ParsedTargetAction): BuilderConditionGroup['action'] {
  if (parsed.family === 'send_alert') return { type: 'send_alert' };
  if (parsed.family === 'status_change') {
    return { type: parsed.status === 'ENABLED' ? 'enable_target' : 'pause_target' };
  }
  if (parsed.family === 'parent_campaign_status') {
    return { type: parsed.status === 'ENABLED' ? 'enable_parent_campaign' : 'pause_parent_campaign' };
  }
  if (parsed.family === 'parent_campaign_budget') {
    const method = parsed.method === 'set_suggested' ? 'set_fixed' : parsed.method;
    return { type: BUDGET_METHOD_TO_TYPE[method], value: parsed.value };
  }
  if (parsed.method === 'set_suggested') return { type: 'set_suggested_bid' };
  return { type: BID_METHOD_TO_TYPE[parsed.method], value: parsed.value };
}

export function entityTypeLabel(entityType: string): string {
  return AUTOMATION_RULE_CHIPS.find((c) => c.id === entityType)?.label || entityType;
}
